import { Injectable } from '@angular/core';
import { AppPersistantStateService, StoreNames } from '../../core/state/app-state.service';
import { RuleSet } from 'query-builder/query.interfaces';
import { uuid } from 'helpers';

export interface IQueryStorageItem {
  campaignId: string;
  clientQueryId: string;
  query: RuleSet;
  updated_at: number;
}

/**
 * Handles preloading of data, is triggered when the user is logged in
 */
@Injectable({
  providedIn: 'root'
})
export class QueryStorageService {

  /**
   * Maximum size in KB for queries in storage.
   * When limit is reached, oldest query will be dropped
   * and replaced with newest query.
   */

  maxQueryStorageKb = 1000;

  constructor(private appStateService: AppPersistantStateService) {
  }

  saveQuery(campaignId: string, query: RuleSet, clientQueryId?: string): IQueryStorageItem {
    let queries = this.getQueries();

    if (clientQueryId && this.getQuery(clientQueryId, campaignId)) {
      queries = this.getQueries()
        .filter(q => q.campaignId === campaignId)
        .filter(q => q.clientQueryId !== clientQueryId);
    }

    const queryObject = {
      campaignId,
      query,
      clientQueryId: clientQueryId || uuid(),
      updated_at: new Date().getTime()
    };

    queries.unshift(queryObject);

    // get the kbs used by queries
    if ((JSON.stringify(queries).length * 2) / 1024 > this.maxQueryStorageKb) {
      queries.pop();
    }

    this.updateAppQueryStateService(queries);
    return queryObject;
  }

  /**
   * Returns a deep copy of the query items in storage.
   */
  getQueries(): IQueryStorageItem[] {
    return this.appStateService.getValue<IQueryStorageItem[]>(StoreNames.Queries) || [];
  }

  /**
   * Returns a query by the UUID and if provided, restricts the result to only the campaign id.     *
   *
   * @param clientQueryId UUID of the query
   * @param campaignId If provided, will only return the query if the campaignId of the store query matches
   */
  getQuery(clientQueryId: string, campaignId?: string) {
    const queries = this.appStateService.getValue<IQueryStorageItem[]>(StoreNames.Queries) || [];

    const result = queries.find(c => c.clientQueryId === clientQueryId);

    if (result && campaignId && result.campaignId !== campaignId) {
      return null;
    }

    return result;
  }

  clearQueries() {
    this.updateAppQueryStateService([]);
  }

  removeQuery(clientQueryId: string, campaignId: string) {
    const queries = this.getQueries().filter(q => q.clientQueryId !== clientQueryId && q.campaignId !== campaignId);
    this.updateAppQueryStateService(queries);
  }

  private updateAppQueryStateService(queries: IQueryStorageItem[]) {
    this.appStateService.update<IQueryStorageItem[]>(StoreNames.Queries, queries);
  }
}
